import { EMAIL_CONSTRAINT as getEmailFormatError } from 'bundles/authentication-common/constants/constraints';
import type { Invitee } from 'bundles/enterprise-admin-learners/components/invite-learners/types';

import _t from 'i18n!nls/enterprise-admin-learners';

export const MAX_EMAIL_INVITE_ROWS = 10;

export type EmailInviteRow = Invitee & { id: string };
export type EmailInviteRowErrors = { nameError?: string; emailError?: string };

let nextRowId = 0;

/** A fresh, empty row for the email-invite form. `id` is UI-only — stripped before submission. */
export const createEmptyEmailInviteRow = (): EmailInviteRow => ({
  id: `email-invite-row-${nextRowId++}`,
  name: '',
  email: '',
});

/**
 * Submit-time-only validation for the email-invite rows: required name, required/valid-shape
 * email (via the shared auth email constraint), and duplicate-email detection across rows.
 * Returns an error map keyed by row id — rows with no errors are omitted from the map.
 */
export const getEmailInviteRowErrors = (rows: EmailInviteRow[]): Record<string, EmailInviteRowErrors> => {
  const errors: Record<string, EmailInviteRowErrors> = {};
  const seenEmails = new Set<string>();

  rows.forEach((row) => {
    const trimmedName = row.name.trim();
    const trimmedEmail = row.email.trim();
    const rowErrors: EmailInviteRowErrors = {};

    if (!trimmedName) {
      rowErrors.nameError = _t('Name is required. Enter a name to continue.');
    }

    const emailFormatError = getEmailFormatError(trimmedEmail);
    if (emailFormatError) {
      rowErrors.emailError = emailFormatError;
    } else {
      const normalizedEmail = trimmedEmail.toLowerCase();
      if (seenEmails.has(normalizedEmail)) {
        rowErrors.emailError = _t('This email has already been added.');
      }
      seenEmails.add(normalizedEmail);
    }

    if (rowErrors.nameError || rowErrors.emailError) {
      errors[row.id] = rowErrors;
    }
  });

  return errors;
};
